import ProgressBar from "@components/ProgressBar/ProgressBar";
import { Slider } from "@components/ui/slider";
import { getApplicantCountByThreshold } from "@endpoints/job.service";
import { Check, CircleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const InsightAndRules = () => {
  const { jobId } = useParams();

  const [threshold, setThreshold] = useState(70);

  const fetchApplicantCounts = useCallback(async () => {
    if (!jobId || !threshold) return;
    const response = await getApplicantCountByThreshold({
      jobId,
      threshold,
    });

  }, [jobId, threshold]);

  useEffect(() => {
    if (!fetchApplicantCounts) return;
    fetchApplicantCounts();
  }, [fetchApplicantCounts]);
  

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3.5 pt-4 overflow-y-scroll no-scrollbar">
      {/* Row 1 */}
      <div className="flex flex-col border rounded-md">
        <div className="flex items-center justify-between rounded-md bg-slate-50 p-4">
          <div className="flex flex-col">
            <span className="font-bold text-light-black text-xss tracking-widest uppercase">
              matching rule
            </span>
            <span className="font-semibold text-md text-black">
              Set the priority-review threshold
            </span>
            <span className="font-medium text-xss text-light-black">
              Preview how many applicants will appear under Priority Applicants
              before saving a change.
            </span>
          </div>
          <div className="flex items-center border rounded-md">
            <div className="flex flex-col px-3 py-1.5 w-28">
              <span className="uppercase font-bold text-xsss text-light-black">
                saved rule
              </span>
              <span className="font-semibold text-md">75%</span>
            </div>
            <div className="flex flex-col px-3 py-1.5 w-28 border-l">
              <span className="uppercase font-bold text-xsss text-light-black">
                preview
              </span>
              <span className="font-semibold text-md">75%</span>
            </div>
          </div>
        </div>

        <div className="p-3 border-t">
          <div className="flex items-center justify-between">
            <span className="font-bold text-xss text-slate-600">
              Match score threshold
            </span>
            <span className="text-xsss font-medium text-light-black">
              Applicants at or above this score enter Priority Applicants. Lower
              scores remain available under Below threshold.
            </span>
          </div>

          <Slider
            defaultValue={[60]}
            min={50}
            max={90}
            step={5}
            className="mt-4 mb-2"
          />

          <div className="flex items-center justify-between pl-1">
            <span className="font-medium text-light-black text-xss">50%</span>
            <span className="font-medium text-light-black text-xss">60%</span>
            <span className="font-medium text-light-black text-xss">70%</span>
            <span className="font-medium text-light-black text-xss">80%</span>
            <span className="font-medium text-light-black text-xss">90%</span>
          </div>

          <div className="grid grid-cols-3 rounded-md border mt-4">
            <div className="flex flex-col p-3 gap-0.5">
              <span className="font-bold tracking-wider uppercase text-xsss text-light-black">
                Projected priority applicants
              </span>
              <span className="font-semibold text-black text-xss">
                <span className="font-semibold pr-1 text-xl">18</span>candidates
              </span>
              <span className="font-medium text-xsss text-light-black">
                Available for priority review
              </span>
            </div>
            <div className="flex flex-col p-3 border-l gap-0.5">
              <span className="font-bold tracking-wider uppercase text-xsss text-light-black">
                Below threshold
              </span>
              <span className="font-semibold text-black text-xss">
                <span className="font-semibold pr-1 text-xl">18</span>candidates
              </span>
              <span className="font-medium text-xsss text-light-black">
                Still visible and reviewable
              </span>
            </div>
            <div className="flex flex-col p-3 border-l gap-0.5">
              <span className="font-bold tracking-wider uppercase text-xsss text-light-black">
                Preferred queue size
              </span>
              <span className="font-semibold text-black text-xss">
                <span className="font-semibold pr-1 text-xl">18</span>candidates
              </span>
              <span className="font-medium text-xsss text-light-black">
                Used to monitor queue health
              </span>
            </div>
          </div>

          <div className="flex gap-2 items-center border-l-[3px] border border-l-orange-500 rounded-md bg-orange-50 p-3 mt-3">
            <CircleAlert color="red" strokeWidth={1.3} />
            <div className="flex flex-col">
              <span className="font-bold text-xss">
                Queue below preferred volume
              </span>
              <span className="font-medium text-xsss text-light-black">
                7 more candidates are needed at this threshold.
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t p-3">
          <span className="text-xsss font-medium text-light-black">
            <span className="font-bold text-content-muted">Preview only.</span>{" "}
            Changing this rule never rejects or messages a candidate.
          </span>
          <div className="flex items-center gap-2">
            <button className="h-8 px-2.5 rounded-md border font-semibold text-xss text-content-muted hover:bg-gray-100">
              Review 41 filtered candidates
            </button>
            <button className="h-8 px-2.5 rounded-md border font-semibold text-xss bg-black hover:bg-black/70 text-white">
              Apply threshold
            </button>
          </div>
        </div>
      </div>

      {/* Row 2 */}
      <div className="flex gap-4">
        {/* Col 1 */}
        <div className="border rounded-md p-4 flex-1">
          <div className="flex flex-col gap-0.5">
            <span className="font-bold text-light-black text-xss tracking-wider uppercase">
              Applicant distribution
            </span>
            <span className="font-semibold text-sm text-black">
              Where the 59 match scores sit
            </span>
            <span className="font-medium text-xsss text-light-black">
              See the available review volume across each score band.
            </span>
          </div>

          <div className="flex flex-col gap-3 border-t py-2.5 mt-2.5">
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-light-black text-xss">
                  90% and above
                </span>
                <span className="font-semibold text-content-secondary text-xss">
                  4 candidates
                </span>
              </div>
              <ProgressBar total={100} value={10} />
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-light-black text-xss">
                  90% and above
                </span>
                <span className="font-semibold text-content-secondary text-xss">
                  4 candidates
                </span>
              </div>
              <ProgressBar total={100} value={10} />
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-light-black text-xss">
                  90% and above
                </span>
                <span className="font-semibold text-content-secondary text-xss">
                  4 candidates
                </span>
              </div>
              <ProgressBar total={100} value={10} />
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-light-black text-xss">
                  90% and above
                </span>
                <span className="font-semibold text-content-secondary text-xss">
                  4 candidates
                </span>
              </div>
              <ProgressBar total={100} value={10} />
            </div>
          </div>

          <span className="font-medium text-xsss text-light-black">
            Based on the candidate-approved evidence currently available for
            this job.
          </span>
        </div>
        {/* Col 2 */}
        <div className="border rounded-md p-4 flex-1">
          <div className="flex flex-col gap-0.5">
            <span className="font-bold text-light-black text-xss tracking-wider uppercase">
              Evidence patterns
            </span>
            <span className="font-semibold text-sm text-black">
              Most common gaps against the role
            </span>
            <span className="font-medium text-xsss text-light-black">
              Use these patterns to understand the pool—not to make automatic
              decisions.
            </span>
          </div>

          <div className="flex flex-col border-y my-2">
            <div className="flex items-center py-2.5 gap-2">
              <div className="h-8 w-8 rounded-full border flex items-center justify-center text-xss font-semibold">
                16
              </div>
              <div className="flex flex-col">
                <span className="text-xsss font-bold text-content-muted">
                  Incident-response depth
                </span>
                <span className="text-xsss font-medium text-light-black">
                  Below the published 5+ year requirement
                </span>
              </div>
            </div>
            <div className="flex items-center py-2.5 gap-2 border-y">
              <div className="h-8 w-8 rounded-full border flex items-center justify-center text-xss font-semibold">
                16
              </div>
              <div className="flex flex-col">
                <span className="text-xsss font-bold text-content-muted">
                  Incident-response depth
                </span>
                <span className="text-xsss font-medium text-light-black">
                  Below the published 5+ year requirement
                </span>
              </div>
            </div>
            <div className="flex items-center py-2.5 gap-2">
              <div className="h-8 w-8 rounded-full border flex items-center justify-center text-xss font-semibold">
                16
              </div>
              <div className="flex flex-col">
                <span className="text-xsss font-bold text-content-muted">
                  Incident-response depth
                </span>
                <span className="text-xsss font-medium text-light-black">
                  Below the published 5+ year requirement
                </span>
              </div>
            </div>
          </div>

          <span className="font-medium text-xsss text-light-black">
            Open a candidate’s View skills match page to inspect the evidence
            behind their result.
          </span>
        </div>
      </div>

      {/* Row 3 */}
      <div className="flex items-center border border-l-[4px] border-l-slate-600 rounded-md p-4 gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-full border border-light-grey shrink-0">
          <Check size={14} />
        </div>
        <div className="flex flex-col">
          <span className="font-bold text-light-black text-xsss tracking-widest uppercase">
            Decision safeguard
          </span>
          <span className="font-semibold text-sm text-black mt-1">
            The Agent remains in control
          </span>
          <span className="font-medium text-xss text-light-black">
            Matching organises the review queue; it does not make a recruitment
            decision. Every below-threshold candidate remains retrievable and no
            status or message changes without an Agent action.{" "}
          </span>
        </div>
      </div>
    </div>
  );
};

export default InsightAndRules;
